'use client';
import { type ReactNode } from 'react';
import { useTooltipAnchor } from './shadcn/useTooltipAnchor';
import { AVATAR_STACK, AVATAR_STACK_CHIP as CHIP, AVATAR_STACK_SIZE } from './avatarStack';
import { Tooltip, TooltipAnchor, TooltipContent } from './shadcn/tooltip';

const MAX_MARKS = 6;
// Same diameter, overlap, ring and chip as a project's team.
const CHIP_SIZE = { width: AVATAR_STACK_SIZE, height: AVATAR_STACK_SIZE };

export interface Mark {
  node: ReactNode;
  /** Names this one mark. When every mark carries a label the strip gives each its own tooltip and makes
   *  it focusable; when none does, the whole strip shares one tooltip showing the strip's `label`. */
  label?: string;
}

/** A quiet row of small round marks that overlap like a project's team, capped with a "+N" chip. The
 *  `label` is the count in words: it is the screen-reader text and, for a strip whose marks are unlabelled,
 *  the hover tip. Icons inside the marks are decorative. A strip whose marks all name themselves may omit
 *  `label` when the count already stands as visible text beside it (`SelectionSummary`), so it is not read
 *  out twice. Consumers: the Users directory (`UserGrantStrip`, one group tooltip), the provider rows in
 *  Settings and model selection summaries (one mark per model, each named on hover). */
export function MarkStrip({ kind, label, marks }: { kind: string; label?: string; marks: readonly Mark[] }) {
  const shown = marks.slice(0, MAX_MARKS);
  const hidden = marks.length - shown.length;
  const overflow = hidden > 0 ? <span aria-hidden className={`${CHIP} text-small-emphasis tabular-nums`} style={CHIP_SIZE}>+{hidden}</span> : null;

  if (marks.every((mark) => mark.label !== undefined)) {
    return (
      <span className="inline-flex" data-mark-strip={kind}>
        <span className={`flex ${AVATAR_STACK}`}>
          {shown.map((mark, index) => <LabelledMark key={index} mark={mark} />)}
          {overflow}
        </span>
        {label ? <span className="sr-only">{label}</span> : null}
      </span>
    );
  }
  if (!label) throw new Error(`MarkStrip "${kind}": marks without their own labels need the strip label`);
  return <GroupTooltipStrip kind={kind} label={label} shown={shown} overflow={overflow} />;
}

/** The count revealed on hover. The anchor is not focusable on purpose: the Users card is already one
 *  control named by its title button, and the same count is read out through the hidden text. */
function GroupTooltipStrip({ kind, label, shown, overflow }: { kind: string; label: string; shown: readonly Mark[]; overflow: ReactNode }) {
  const anchor = useTooltipAnchor();
  const { open } = anchor;
  return (
    <Tooltip open={open} onOpenChange={anchor.onOpenChange}>
      <TooltipAnchor asChild>
        <span className="inline-flex" {...anchor.hover}>
          <span aria-hidden className={`flex ${AVATAR_STACK}`} data-mark-strip={kind}>
            {shown.map((mark, index) => <span key={index} className={CHIP} style={CHIP_SIZE}>{mark.node}</span>)}
            {overflow}
          </span>
          <span className="sr-only">{label}</span>
        </span>
      </TooltipAnchor>
      <TooltipContent side="top" className="w-auto whitespace-nowrap px-2 py-1">{label}</TooltipContent>
    </Tooltip>
  );
}

/** One mark that names itself: an image with its label, reachable by hover, tap and keyboard focus. */
function LabelledMark({ mark }: { mark: Mark }) {
  const anchor = useTooltipAnchor();
  const { open } = anchor;
  return (
    <Tooltip open={open} onOpenChange={anchor.onOpenChange}>
      <TooltipAnchor asChild>
        <span
          role="img"
          aria-label={mark.label}
          tabIndex={0}
          style={CHIP_SIZE}
          className={`${CHIP} focus-visible:relative focus-visible:z-[var(--z-lift)] focus-visible:outline-none focus-visible:ring-ring/70`}
          {...anchor.hover}
          {...anchor.focus}
          // A tap focuses the mark after the pointer events opened the tip, and that focus move reads as an
          // outside interaction that dismisses it; the click that follows reopens it, as on the project team.
          onClick={anchor.show}
        >
          {mark.node}
        </span>
      </TooltipAnchor>
      <TooltipContent side="top" className="w-auto max-w-[min(--spacing(80),calc(100vw-var(--spacing)*6))] break-all px-2 py-1">{mark.label}</TooltipContent>
    </Tooltip>
  );
}
